import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Search, SlidersHorizontal, X } from "lucide-react";
import { api, unwrapArray } from "@/lib/api";
import { cn } from "@/lib/utils";
import { fieldFilterLabel, type FieldFilterValue, type FilterFieldDef } from "@/lib/useFieldFilters";
import { FieldTypeIcon } from "./FieldTypeIcon";

const MAX_FILTERS = 10;

type Editing = { index: number | null; field: FilterFieldDef } | null;

/**
 * "+ Add filter": the filters a person added on any field, shown as chips after the fixed filter pills.
 * Click "+ Add filter" → pick a field → a condition and a value → Apply. Click a chip to change it, × to remove it.
 */
export function FieldFilterChips({
  fields,
  value,
  onChange,
  suggestUrl,
  hideIndexes,
  addLabel = "Add filter",
}: {
  fields: FilterFieldDef[];
  value: FieldFilterValue[];
  onChange: (next: FieldFilterValue[]) => void;
  /** e.g. /api/Lead/FilterValues: values already in use, for text fields */
  suggestUrl?: string;
  /** Filters (by position) that already show as a quick filter, so they are not listed twice */
  hideIndexes?: Set<number>;
  addLabel?: string;
}) {
  const [open, setOpen] = useState<"pick" | "edit" | null>(null);
  const [editing, setEditing] = useState<Editing>(null);
  const [anchor, setAnchor] = useState<number | "add">("add");
  const rootRef = useRef<HTMLDivElement>(null);
  const byKey = useMemo(() => new Map(fields.map((f) => [f.key, f])), [fields]);

  // Close on an outside click or Escape
  useEffect(() => {
    if (!open) return;
    const close = () => {
      setOpen(null);
      setEditing(null);
    };
    const onDown = (e: MouseEvent) => rootRef.current && !rootRef.current.contains(e.target as Node) && close();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const shown = value.map((f, i) => ({ f, i, def: byKey.get(f.field) })).filter((x) => x.def && !hideIndexes?.has(x.i));
  const full = value.length >= MAX_FILTERS;

  const save = (f: FieldFilterValue) => {
    const next = [...value];
    if (editing?.index !== null && editing?.index !== undefined) next[editing.index] = f;
    else next.push(f);
    onChange(next);
    setOpen(null);
    setEditing(null);
  };

  const panel = (
    <div className="z-50 w-72 overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-xl animate-in fade-in-50 zoom-in-95 duration-100 max-sm:fixed max-sm:inset-x-4 max-sm:bottom-4 max-sm:w-auto sm:absolute sm:left-0 sm:top-full sm:mt-1.5">
      {open === "pick" ? (
        <FieldPicker
          fields={fields}
          onPick={(item) => {
            setEditing({ index: null, field: byKey.get(item.key)! });
            setOpen("edit");
          }}
        />
      ) : editing ? (
        <ConditionEditor
          field={editing.field}
          initial={editing.index !== null ? value[editing.index] : undefined}
          suggestUrl={suggestUrl}
          onBack={editing.index === null ? () => setOpen("pick") : undefined}
          onApply={save}
          onCancel={() => {
            setOpen(null);
            setEditing(null);
          }}
        />
      ) : null}
    </div>
  );

  return (
    <div ref={rootRef} className="contents">
      {shown.map(({ f, i, def }) => (
        <div key={`${f.field}-${i}`} className="relative">
          <span className={cn("inline-flex h-7 items-center gap-1 rounded-md bg-accent pl-2.5 pr-1 text-xs font-medium text-accent-foreground", "max-w-[280px]", open && anchor === i && "ring-2 ring-primary/40")}>
            <button
              type="button"
              className="min-w-0 cursor-pointer truncate"
              title={fieldFilterLabel(def, f)}
              onClick={() => {
                setAnchor(i);
                setEditing({ index: i, field: def! });
                setOpen(open && anchor === i ? null : "edit");
              }}
            >
              {fieldFilterLabel(def, f)}
            </button>
            <button type="button" aria-label={`Remove filter: ${fieldFilterLabel(def, f)}`} onClick={() => onChange(value.filter((_, j) => j !== i))} className="flex size-5 shrink-0 cursor-pointer items-center justify-center rounded hover:bg-primary/15">
              <X className="size-3" />
            </button>
          </span>
          {open && anchor === i && panel}
        </div>
      ))}
      <div className="relative">
        <button
          type="button"
          disabled={full}
          title={full ? `At most ${MAX_FILTERS} filters` : undefined}
          onClick={() => {
            setAnchor("add");
            setEditing(null);
            setOpen(open && anchor === "add" ? null : "pick");
          }}
          aria-expanded={open !== null && anchor === "add"}
          className={cn(
            "inline-flex h-7 cursor-pointer items-center gap-1 rounded-md px-2 text-xs font-medium text-primary transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50",
            open && anchor === "add" && "bg-muted",
          )}
        >
          <SlidersHorizontal className="size-3.5" />
          {addLabel}
        </button>
        {open && anchor === "add" && panel}
      </div>
    </div>
  );
}

/** Something the field picker can list: a field of the list, or one of the page's own standard filters */
export interface PickerItem {
  key: string;
  label: string;
  group: string;
  type: FilterFieldDef["type"];
}

/** Step 1: choose the field (searchable, grouped, with a type icon); `used` rows are shown but can't be picked */
export function FieldPicker({ fields, onPick, used, title }: { fields: PickerItem[]; onPick: (f: PickerItem) => void; used?: Set<string>; title?: string }) {
  const [q, setQ] = useState("");
  const list = fields.filter((f) => !q.trim() || f.label.toLowerCase().includes(q.trim().toLowerCase()));
  const groups = list.reduce<Record<string, PickerItem[]>>((acc, f) => ((acc[f.group] ||= []).push(f), acc), {});
  return (
    <div className="flex max-h-[380px] flex-col">
      {title && <div className="px-3 pb-0 pt-3 text-sm font-semibold">{title}</div>}
      <div className="relative border-b border-border p-2">
        <Search className="pointer-events-none absolute left-4 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search fields" className="h-8 w-full rounded-md border border-border bg-background pl-7 pr-2 text-xs outline-none focus:ring-1 focus:ring-primary" />
      </div>
      <div className="flex-1 overflow-y-auto py-1">
        {Object.entries(groups).map(([group, items]) => (
          <div key={group}>
            <div className="px-3 pb-0.5 pt-2 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">{group}</div>
            {items.map((f) => (
              <button
                key={f.key}
                type="button"
                disabled={used?.has(f.key)}
                onClick={() => onPick(f)}
                className="flex w-full cursor-pointer items-center gap-2.5 px-3 py-1.5 text-left text-xs hover:bg-muted disabled:cursor-default disabled:opacity-45 disabled:hover:bg-transparent"
              >
                <FieldTypeIcon type={f.type} />
                <span className="min-w-0 flex-1 truncate">{f.label}</span>
                {used?.has(f.key) && <span className="text-[10px] text-muted-foreground">Added</span>}
              </button>
            ))}
          </div>
        ))}
        {!list.length && <div className="px-3 py-4 text-center text-xs text-muted-foreground">No field matches</div>}
      </div>
    </div>
  );
}

const inputCls = "h-8 w-full rounded-md border border-border bg-background px-2 text-xs outline-none focus:ring-1 focus:ring-primary";

/** Step 2: the condition and its value; Apply only when the value makes sense */
export function ConditionEditor({
  field,
  initial,
  suggestUrl,
  onBack,
  onApply,
  onCancel,
}: {
  field: FilterFieldDef;
  initial?: FieldFilterValue;
  suggestUrl?: string;
  onBack?: () => void;
  onApply: (f: FieldFilterValue) => void;
  onCancel: () => void;
}) {
  const [op, setOp] = useState(initial?.op || field.conditions[0]!.op);
  const cond = field.conditions.find((c) => c.op === op) || field.conditions[0]!;
  const init = initial?.op === op ? initial.value : undefined;
  const [one, setOne] = useState(init !== undefined && !Array.isArray(init) ? String(init) : "");
  const [a, setA] = useState(Array.isArray(init) ? String(init[0] ?? "") : "");
  const [b, setB] = useState(Array.isArray(init) ? String(init[1] ?? "") : "");
  const [many, setMany] = useState<string[]>(Array.isArray(init) ? init.map(String) : []);
  const [tagText, setTagText] = useState(field.type === "tags" && Array.isArray(init) ? init.join(", ") : "");
  const [suggestions, setSuggestions] = useState<{ value: string; count: number }[]>([]);

  // Values already used in this field, for the value box (text fields only)
  useEffect(() => {
    if (!suggestUrl || !field.suggest || cond.arity !== "one") return;
    const t = setTimeout(() => {
      api
        .get(`${suggestUrl}?field=${encodeURIComponent(field.key)}&search=${encodeURIComponent(one.trim())}`)
        .then((res) => setSuggestions(unwrapArray(res)))
        .catch(() => setSuggestions([]));
    }, 250);
    return () => clearTimeout(t);
  }, [suggestUrl, field.key, field.suggest, cond.arity, one]);

  const num = (s: string) => s.trim() !== "" && Number.isFinite(Number(s));
  const build = (): FieldFilterValue | null => {
    const base = { field: field.key, op: cond.op };
    switch (cond.arity) {
      case "none":
        return base;
      case "one":
        if (field.type === "number") return num(one) ? { ...base, value: Number(one) } : null;
        return one.trim() ? { ...base, value: one.trim() } : null;
      case "two":
        if (field.type === "number") return num(a) && num(b) ? { ...base, value: [Number(a), Number(b)] } : null;
        return a && b ? { ...base, value: [a, b] } : null;
      case "days":
        return /^\d+$/.test(one) && Number(one) >= 1 && Number(one) <= 3650 ? { ...base, value: Number(one) } : null;
      case "many": {
        const list = field.type === "tags" ? tagText.split(",").map((s) => s.trim()).filter(Boolean) : many;
        return list.length ? { ...base, value: list } : null;
      }
    }
  };
  const result = build();
  const inputType = field.type === "number" ? "number" : field.type === "date" ? "date" : "text";

  return (
    <form
      className="space-y-2.5 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (result) onApply(result);
      }}
    >
      <div className="flex items-center gap-1.5">
        {onBack && (
          <button type="button" onClick={onBack} aria-label="Choose another field" className="flex size-6 cursor-pointer items-center justify-center rounded hover:bg-muted">
            <ArrowLeft className="size-3.5" />
          </button>
        )}
        <span className="text-xs font-semibold">{field.label}</span>
      </div>
      <select value={op} onChange={(e) => setOp(e.target.value)} className={inputCls} aria-label="Condition">
        {field.conditions.map((c) => (
          <option key={c.op} value={c.op}>
            {c.label}
          </option>
        ))}
      </select>

      {cond.arity === "one" && (
        <>
          <input autoFocus type={inputType} value={one} onChange={(e) => setOne(e.target.value)} placeholder={field.type === "number" ? "Number" : "Value"} className={inputCls} />
          {suggestions.length > 0 && (
            <div className="max-h-36 overflow-y-auto rounded-md border border-border/70">
              {suggestions.map((s) => (
                <button key={s.value} type="button" onClick={() => setOne(s.value)} className={cn("flex w-full cursor-pointer items-center justify-between px-2 py-1 text-left text-xs hover:bg-muted", s.value === one && "bg-muted font-medium")}>
                  <span className="truncate">{s.value}</span>
                  <span className="font-mono text-[10.5px] text-muted-foreground">{s.count}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
      {cond.arity === "two" && (
        <div className="grid grid-cols-2 gap-1.5">
          <input autoFocus type={inputType} value={a} onChange={(e) => setA(e.target.value)} placeholder="From" className={inputCls} aria-label="From" />
          <input type={inputType} value={b} onChange={(e) => setB(e.target.value)} placeholder="To" className={inputCls} aria-label="To" />
        </div>
      )}
      {cond.arity === "days" && (
        <div className="flex items-center gap-2">
          <input autoFocus type="number" min={1} max={3650} value={one} onChange={(e) => setOne(e.target.value)} className={inputCls} aria-label="Number of days" />
          <span className="shrink-0 text-xs text-muted-foreground">days</span>
        </div>
      )}
      {cond.arity === "many" && field.type === "enum" && (
        <div className="max-h-44 space-y-0.5 overflow-y-auto">
          {(field.options || []).map((o) => (
            <label key={o.value} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs hover:bg-muted">
              <input type="checkbox" className="size-3.5 accent-primary" checked={many.includes(o.value)} onChange={(e) => setMany(e.target.checked ? [...many, o.value] : many.filter((x) => x !== o.value))} />
              {o.label}
            </label>
          ))}
        </div>
      )}
      {cond.arity === "many" && field.type === "tags" && (
        <input autoFocus value={tagText} onChange={(e) => setTagText(e.target.value)} placeholder="e.g. vip, export" className={inputCls} aria-label="Tags, separated by commas" />
      )}

      <div className="flex justify-end gap-1.5 pt-0.5">
        <button type="button" onClick={onCancel} className="h-7 cursor-pointer rounded-md px-2.5 text-xs hover:bg-muted">
          Cancel
        </button>
        <button type="submit" disabled={!result} className="h-7 cursor-pointer rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50">
          Apply
        </button>
      </div>
    </form>
  );
}
